import { useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Chip, DataTable } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import apiClient from '@/services/apiClient';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { useMembers } from '../lib/useManagerData';
import { SettingsRail } from './SettingsRail';

/**
 * Clinic manager · Settings ▸ Subscription — `g_set_subs.dc.html`.
 *
 * READ-ONLY FOR THE MANAGER. `GET /me/billing/subscription` and `/invoices`
 * answer the owner and the clinic manager; every write (checkout, renew,
 * portal, add-ons) is still owner-only. The plan card and the invoice list
 * come from those two reads; there is no change / cancel / pay action here.
 */

interface PlanSummary {
  tier: string;
  status: string;
  seatsPurchased: number;
  trialEndsAt: string | null;
  billingMode: 'auto' | 'prepaid';
  currency: string | null;
  paymentState: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  viewerIsOwner?: boolean;
}

interface InvoiceRow {
  id: string;
  number: string | null;
  amountDueMinor: number;
  amountPaidMinor: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  pdfUrl: string | null;
  hostedInvoiceUrl: string | null;
  createdAt: string;
}

function usePlan() {
  const q = useQuery({
    queryKey: ['cm-subscription'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<PlanSummary | null> => {
      const res = await apiClient.get('/me/billing/subscription');
      return (res.data?.data ?? res.data ?? null) as PlanSummary | null;
    },
  });
  return { ...q, plan: q.data ?? null };
}

function useInvoices() {
  const q = useQuery({
    queryKey: ['cm-subscription-invoices'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<InvoiceRow[]> => {
      const res = await apiClient.get('/me/billing/invoices');
      return (res.data?.data ?? res.data ?? []) as InvoiceRow[];
    },
  });
  return { ...q, invoices: q.data ?? [] };
}

const PAGE = 3;
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
const fmtMonth = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

/** "trialing" → "Trialing"; the API's own word, capitalised, never renamed. */
const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function ManagerSubscriptionPage() {
  const go = useNavigate();
  const { plan, isLoading } = usePlan();
  const { invoices } = useInvoices();
  const { members } = useMembers();
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE);

  usePageMeta({ title: 'Subscription · System Settings' });

  const doctors = members.filter((m) => m.roleName === 'Doctor' && m.status === 'active').length;
  const people = members.filter((m) => m.status === 'active').length;
  const status = plan?.status ? titleCase(plan.status) : null;
  const renews = fmtDate(plan?.currentPeriodEnd ?? null);

  const crumb = usePhoneCrumb('Settings', 'Subscription');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: 'Subscription',
      sub: (
        <>
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ System Settings
          </button>
          {' '}<span className="mut">▸</span> Subscription
        </>
      ),
      search: 'Search a setting…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="subscription" />,
    },
    [query],
  );

  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1.3, minWidth: 0 }}>
            <Card kind="accent" style={{ background: 'var(--brand-100)' }}>
              <div className="flex" style={{ gap: 12 }}>
                <div className="grow">
                  <p className="t-title">
                    {isLoading && !plan ? 'Reading this clinic’s plan…'
                      : plan ? `${titleCase(plan.tier)} · ${plan.billingMode === 'auto' ? 'auto-renewing' : 'prepaid'}`
                      : 'Plan not readable here'}
                  </p>
                  {plan ? (
                    <p className="t-sec mut">
                      {people} {people === 1 ? 'person' : 'people'} · {doctors}{' '}
                      {doctors === 1 ? 'doctor' : 'doctors'} · {plan.seatsPurchased} seats
                      {renews ? ` · ${plan.cancelAtPeriodEnd ? 'ends' : 'renews'} ${renews}` : ''}
                    </p>
                  ) : null}
                </div>
                {plan ? (
                  <Chip kind={plan.paymentState === 'active' && !plan.cancelAtPeriodEnd ? 'success' : 'warn'} small>
                    {status}
                  </Chip>
                ) : null}
              </div>
            </Card>

            {matches('invoices') && invoices.length > 0 ? (
              <>
                <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>Invoices</p>
                <DataTable
                  caption="Subscription invoices"
                  columns={[
                    { key: 'inv', label: 'Invoice', width: 14 },
                    { key: 'amt', label: 'Amount', width: 12 },
                    { key: 'paid', label: 'Paid', width: 12 },
                    { key: 'pdf', label: '', width: 6 },
                  ]}
                  rows={invoices.slice(0, shown).map((inv) => {
                    const major = (n: number) => Math.round(n / 100);
                    const url = inv.pdfUrl ?? inv.hostedInvoiceUrl;
                    return {
                      id: inv.id,
                      cells: {
                        inv: <span className="t-strong">{fmtMonth(inv.createdAt)}</span>,
                        amt: <span>{major(inv.amountDueMinor).toLocaleString('en-US')}</span>,
                        paid: inv.status === 'paid'
                          ? <span className="t-sec mut">paid {fmtDate(inv.createdAt)}</span>
                          : <Chip kind="warn" small>{inv.status}</Chip>,
                        pdf: url ? (
                          <a className="w6 brand" href={url} target="_blank" rel="noreferrer">PDF</a>
                        ) : null,
                      },
                    };
                  })}
                />
                <p className="t-sec mut mt8">
                  showing {Math.min(shown, invoices.length)} of {invoices.length}
                  {shown < invoices.length ? (
                    <>
                      {' · '}
                      <button type="button" className="w6 brand"
                        style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}
                        onClick={() => setShown((n) => n + PAGE * 4)}>
                        Load more
                      </button>
                    </>
                  ) : null}
                </p>
              </>
            ) : null}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Card style={{ borderColor: 'var(--warning-600)', background: 'var(--warning-100)' }}>
              <p className="t-sec w6 amb">If a payment fails</p>
              <p className="t-cap sec mt4">
                14 days’ grace with a banner only you see → then read-only → export always works,
                forever. A clinic that stops paying is never held hostage.
              </p>
            </Card>
            <Card>
              <p className="t-ovl mut">The accountant sees</p>
              <p className="t-sec sec mt4">
                these invoices as an expense under Subscriptions, in the clinic’s name — she never
                needs to ask you for a copy.
              </p>
            </Card>
            <Card>
              <p className="t-ovl mut">What the plan counts</p>
              <p className="t-sec sec mt4">
                branches and doctors, not visits and not messages. Messages are the wallet, and the
                wallet is separate on purpose.{' '}
                <button type="button" className="w6 brand"
                  style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
                  onClick={() => go('/clinic-manager/settings/wallet')}>
                  Open the wallet ›
                </button>
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
